import { Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import UploadFile from "@/components/form/file-input";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";

const createProductSchema = z.object({
	name: z.string({
		required_error: "Name is required"
	}).min(3, {
		message: "Name must be at least 3 characters"
	}),
	price: z.coerce.string({
		required_error: "Price is required"
	}).refine(val => !isNaN(Number(val)) && Number(val) > 0, {
		message: "Price must be a number greater than 0"
	}),
	status: z.coerce.string({
		required_error: "Price is required"
	}).min(1, { message: "Status is required" }),
	quantity: z.coerce.string({
		required_error: "Quantity is required"
	}).refine(val => !isNaN(Number(val)) && Number(val) > 0, {
		message: "Quantity must be a number greater than 0"
	}),
	description: z.string({
		required_error: "Description is required"
	}).min(3, {
		message: "Description must be at least 3 characters"
	}),
	photos: z.string({ required_error: "At least one photo is required" }).min(1, {
		message: "At least one photo is required"
	})

});

function CreateProductForm({ isSuccess, isPending, isError, onSubmit, ...props }: z.infer<typeof createProductSchema> & { isSuccess: boolean; isPending?: boolean; isError?: boolean; onSubmit: (product: FormData) => Promise<any> }) {
	const [files, setFiles] = useState<File[] | null>(null);

	const form = useForm<z.infer<typeof createProductSchema>>(
		{
			resolver: zodResolver(createProductSchema),
			defaultValues: {
				...props,
				photos: files ? files[0]?.name : ""
			}
		}
	);

	const handleSubmit = async (data: z.infer<typeof createProductSchema>) => {
		if (!files)
			return;
		const product = new FormData();
		if (files) {
			files.forEach((file, index) => {
				product.append(`files`, file, `image${index}.jpg`);
			});
		}
		Object.entries(data).forEach(([key, value]) => {
			if (key !== "photos")
				product.append(key, value);
		});

		await onSubmit(product);
	};

	useEffect(() => {
		setFiles(null);
		form.reset();
	}, [isSuccess]);

	return (

		<section className="flex flex-col space-y-4">
			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(handleSubmit)}
					className={cn("min-h-[80vh] p-3 bg-white rounded-xl flex flex-col gap-4 flex-wrap h-full", {
					})}
				>
					<h3>Product images</h3>
					<UploadFile
						files={files}
						setFiles={setFiles}
						className="w-  outline-dashed outline-1 outline-primary hover:bg-primary/20"
						name="photos"
						control={form.control}
					/>
					<span className="text-muted-foreground text-sm">(Recommended dimension 930 x 1163)</span>
					<FormField
						control={form.control}
						name="name"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<Input placeholder="Enter product name" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="price"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<Input type="number" placeholder="Enter price" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="quantity"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<Input type="number" placeholder="Enter Quantity" {...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="status"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<Select
										onValueChange={value => field.onChange(value)}
										value={field.value}
									>
										<SelectTrigger>
											<SelectValue placeholder="Select a status" />
										</SelectTrigger>
										<SelectContent>
											<SelectGroup>
												<SelectItem value="ACTIVE">Active</SelectItem>
												<SelectItem value="INACTIVE">In Active</SelectItem>
											</SelectGroup>
										</SelectContent>
									</Select>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<FormField
						control={form.control}
						name="description"
						render={({ field }) => (
							<FormItem>
								<FormControl>
									<Textarea placeholder="Enter product description" className="min-h-[20vh]"{...field} />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
					<div className="flex justify-between">

						<span className="flex gap-2 ml-auto">
							<Button type="submit" disabled={isPending}>
								{isPending
									? (
											<>
												<Loader2 size={20} className="animate-spin" />
												&nbsp; Loading....
											</>
										)

									: "Save Product"}
							</Button>
							<Button
								disabled={isPending}
								onClick={() => {
									form.reset();
									history.back();
								}}
								type="button"
								className="bg-destructive-secondary gap-2 text-destructive hover:bg-destructive-secondary/50"
							>
								<X className="size-4" />
								Cancel
							</Button>
						</span>
					</div>
				</form>
			</Form>
			<Dialog>
				<DialogTrigger asChild type="button">
					<Button variant="link">How to asss</Button>
				</DialogTrigger>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>How to use</DialogTitle>
						<DialogDescription>
							How to use the Intasida App, or how our operators can assist you
						</DialogDescription>
						<div className="flex flex-col gap-2">
							<video
								className="w-full rounded-lg"
								controls
								poster="/video-thumbnail.jpg"
							>
								<source src="/how-to-use.mp4" type="video/mp4" />
								Your browser does not support the video tag.
							</video>
						</div>
					</DialogHeader>
				</DialogContent>
			</Dialog>
		</section>
	);
}

export default CreateProductForm;
